import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import { FileUploadDragAndDrop } from "@components/FormsV2/FileUploadDragAndDrop";
import fileToBase64 from "@utils/fileToBase64";
import {
  useV2CreateDataImportMutation,
  useV2GetAccountQuery,
  V2DataImportResource,
} from "@generated";

export interface ResourceRateImportModalProps {
  show: boolean;
  onClose: () => void;
  onError: (message: string) => void;
  rateTableId: string;
}

const appHost = process.env.REACT_APP_DOORKEEPER_APP_URL;

// Rate-table-scoped resource-rate import, reusing the shared data-import pattern
// (useCreateDataImportMutation + FileUploadDragAndDrop + base64). The
// `resource_rates` DataImporter adapter reads `data_import.context` and requires
// it to be a RateTable, so the import is scoped by context-type/context-id (the
// same contract the legacy admin/resource_rates HAML form uses). Once created it
// hands off to the admin column-mapping page to finish the import.
export const ResourceRateImportModal = ({
  show,
  onClose,
  onError,
  rateTableId,
}: ResourceRateImportModalProps): JSX.Element => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { data: account } = useV2GetAccountQuery({}, {
    skip: !accountSlug,
  });
  const [createDataImport] = useV2CreateDataImportMutation();
  const [uploading, setUploading] = useState(false);

  const accountId = account?.data?.attributes?.["account-id"];

  const handleFileSelect = async (file: File) => {
    if (!file || !accountId) return;
    const encodedFile = await fileToBase64(file);
    if (!encodedFile) return;

    const importData: V2DataImportResource = {
      type: "data-imports",
      attributes: {
        "import-type": "resource_rates",
        // The polymorphic DataImport context is the rate table itself; the
        // context-type enum in the schema is documented as Account|Project|Quote
        // but context_type is a plain writable attribute, so "RateTable" is
        // accepted (matching the legacy resource_rates import form).
        "context-type": "RateTable" as any,
        "context-id": rateTableId,
        "data-file": encodedFile,
      },
      relationships: {
        account: { data: { type: "accounts", id: String(accountId) } },
      },
    };

    setUploading(true);
    try {
      const res = await createDataImport({
        body: { data: importData },
      }).unwrap();
      const importId = res.data?.id;
      onClose();
      // Hand off to the admin import column-mapping screen (Rails app host).
      window.location.assign(`${appHost}/admin/imports/${importId}/edit`);
    } catch {
      setUploading(false);
      onError(
        "Could not start the import. Please check the file and try again."
      );
    }
  };

  return (
    <ScopeStackModal
      modalTitle="Import Resource Rates"
      modalBody={
        <div>
          <div className="instructionalText seafoamText bold">
            Upload a CSV or Excel file of resource rates. Expected columns:
            Resource (required), External Name, Description, Hourly Rate
            (required), Hourly Cost, and Expense Rate. You'll map the columns on
            the next screen.
          </div>
          <FileUploadDragAndDrop handleFileSelect={handleFileSelect} />
          {uploading && <div className="mt-2">Uploading…</div>}
        </div>
      }
      button1Text="Cancel"
      handleButton1Click={onClose}
      button2Text={null}
      handleButton2Click={() => null}
      show={show}
      handleClose={onClose}
    />
  );
};

export default ResourceRateImportModal;
